import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { localeCodes } from "i18n";
import { getSession } from "next-auth/react";

export function getToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken != null) return { accessToken };
    return null;
  }
}

function getProviderId(): string | null {
  if (typeof window == "undefined" || !window.sessionStorage) return null;
  const providerId = sessionStorage?.getItem("selectedProvider");
  if (!providerId || providerId === "") {
    return null;
  }

  return JSON.parse(providerId);
}

export function getAccessLang() {
  if (typeof window !== "undefined" && window.localStorage) {
    const currentLocale = JSON.parse(
      localStorage.getItem("currentLocale") || ""
    );
    const localeData = localeCodes.find(
      (locale) => locale.localeName == currentLocale
    );
    if (localeData) return localeData?.localeCode;
    return "en-US";
  }
}

const SESSION_TTL_MS = 5000;
let cachedSession: { promise: Promise<any>; ts: number } | null = null;

async function fetchSession(): Promise<any> {
  return (await getSession()) || getToken();
}

function getCachedSession(): Promise<any> {
  const now = Date.now();
  if (cachedSession && now - cachedSession.ts < SESSION_TTL_MS) {
    return cachedSession.promise;
  }
  const promise = fetchSession().catch((err) => {
    cachedSession = null;
    throw err;
  });
  cachedSession = { promise, ts: now };
  return promise;
}

async function getHeaders(headers: any) {
  const session: any = await getCachedSession();
  return {
    "Accept-Language": getAccessLang(),
    Authorization: `Bearer ${session?.accessToken}`,
    "EJ-ProviderId": getProviderId(),
    ...headers,
  };
}

async function get<T, H>(requestUrl: string, props?: T, headers?: H) {
  const finalHeaders = await getHeaders(headers);
  const response = await axios.get(requestUrl, {
    headers: finalHeaders,
    ...props,
  });
  return response.data;
}

async function safeGet<T, H, R>(
  requestUrl: string,
  props?: T,
  headers?: H
): Promise<R> {
  const finalHeaders = await getHeaders(headers);
  const response = await axios.get<R>(requestUrl, {
    headers: finalHeaders,
    ...props,
  });
  return response.data;
}

async function post<T, H, C>(
  requestUrl: string,
  body?: T,
  headers?: H,
  config?: C
) {
  const response = await safePost<T, H, C, any>(
    requestUrl,
    body,
    headers,
    config
  );
  return response;
}

async function safePost<T, H, C, R>(
  requestUrl: string,
  body?: T,
  headers?: H,
  config?: C,
  customize?: (requestConfig: AxiosRequestConfig<T>) => AxiosRequestConfig<T>
): Promise<AxiosResponse<R>> {
  const finalHeaders = await getHeaders(headers);
  let requestConfig: AxiosRequestConfig<T> = {
    ...config,
    headers: finalHeaders,
  };
  if (customize) {
    requestConfig = customize(requestConfig);
  }
  const response = await axios.post<R>(requestUrl, body, requestConfig);
  return response;
}

async function put<T, H, C>(
  requestUrl: string,
  body?: T,
  headers?: H,
  config?: C
) {
  const response = await safePut<T, H, C, any>(
    requestUrl,
    body,
    headers,
    config
  );
  return response;
}

async function safePut<T, H, C, R>(
  requestUrl: string,
  body?: T,
  headers?: H,
  config?: C
): Promise<AxiosResponse<R>> {
  const finalHeaders = await getHeaders(headers);
  const response = await axios.put<R>(requestUrl, body, {
    ...config,
    headers: finalHeaders,
  });
  return response;
}

async function patch<T, H, C>(
  requestUrl: string,
  body?: T,
  headers?: H,
  config?: C
) {
  const finalHeaders = await getHeaders(headers);
  const response = await axios.patch(requestUrl, body, {
    ...config,
    headers: finalHeaders,
  });
  return response;
}

async function del<H, C>(requestUrl: string, headers?: H, config?: C) {
  const finalHeaders = await getHeaders(headers);
  const response = await axios.delete(requestUrl, {
    ...config,
    headers: finalHeaders,
  });
  return response;
}

export const axiosFetch = {
  get,
  post,
  put,
  patch,
  del,
  safeGet,
  safePost,
  safePut,
};
